import type {
	BillingProviderConfigMap,
	MollieBillingProviderConfig,
} from "../../../providers/types.ts";
import type { BillingSqlExecutor } from "../../../subject/repository.ts";
import { defaultBillingCredentialReference } from "../providers/connection-binding.ts";
import { deriveConfiguredProviderAccountReference } from "./identity.ts";

export interface MaterializedBillingConnection {
	accountReference: string;
	credentialReference: string;
	id: string;
	provider: "mollie";
}

export interface MaterializeConfiguredBillingConnectionsResult {
	connections: readonly MaterializedBillingConnection[];
}

export interface ConfiguredBillingConnectionIntent {
	accountReference: string;
	credentialKind: string;
	credentialReference: string;
	environment: "test" | "live";
	provider: "mollie";
	providerAccountId: string;
}

function billingEnvironment(testMode: boolean | undefined): "test" | "live" {
	return testMode === false ? "live" : "test";
}

function mollieCredentialKind(config: MollieBillingProviderConfig): string {
	if (config.credentialKind != null) {
		return config.credentialKind;
	}
	if ("accessToken" in config) {
		return "advanced_access_token";
	}
	return "api_key";
}

export function configuredBillingConnectionIntents(input: {
	applicationId: string;
	configuredProviders?: BillingProviderConfigMap;
	provider?: "mollie";
	testMode?: boolean;
}): readonly ConfiguredBillingConnectionIntent[] {
	const applicationId = input.applicationId.trim();
	if (applicationId.length === 0) {
		return [];
	}
	const environment = billingEnvironment(input.testMode);
	const intents: ConfiguredBillingConnectionIntent[] = [];
	const mollie = input.configuredProviders?.mollie;
	if (mollie != null && (input.provider == null || input.provider === "mollie")) {
		const reference = defaultBillingCredentialReference("mollie");
		const identity = deriveConfiguredProviderAccountReference({
			applicationId,
			environment,
			provider: "mollie",
			scope: "organization",
		});
		intents.push({
			accountReference: identity,
			credentialKind: mollieCredentialKind(mollie),
			credentialReference: reference,
			environment,
			provider: "mollie",
			providerAccountId: identity,
		});
	}
	const accounts = input.configuredProviders?.mollieAccounts;
	if (
		accounts != null &&
		(input.provider == null || input.provider === "mollie")
	) {
		for (const [accountId, config] of Object.entries(accounts)) {
			if (config == null) {
				continue;
			}
			const slot = accountId.trim();
			if (slot.length === 0) {
				continue;
			}
			const identity = deriveConfiguredProviderAccountReference({
				applicationId,
				environment,
				provider: "mollie",
				scope: slot,
			});
			intents.push({
				accountReference: identity,
				credentialKind: mollieCredentialKind(config),
				credentialReference: `providers.mollie:${slot}`,
				environment,
				provider: "mollie",
				providerAccountId: identity,
			});
		}
	}
	return intents;
}

const UPSERT_CONNECTION_SQL = `
INSERT INTO billing.billing_provider_connections (
	owner_kind,
	owner_id,
	provider,
	mode,
	environment,
	status,
	credential_kind,
	credential_reference,
	provider_account_id,
	account_reference,
	scopes,
	config,
	metadata
)
VALUES (
	'tenant',
	$1,
	$2,
	$3,
	$3,
	'active',
	$4,
	$5,
	$6,
	$6,
	'[]'::jsonb,
	'{}'::jsonb,
	jsonb_build_object('managedBy', 'application_config')
)
ON CONFLICT (provider, account_reference, environment)
WHERE deleted_at IS NULL
DO UPDATE SET
	status = 'active',
	credential_kind = EXCLUDED.credential_kind,
	credential_reference = EXCLUDED.credential_reference,
	mode = EXCLUDED.mode,
	environment = EXCLUDED.environment,
	scopes = EXCLUDED.scopes,
	config = EXCLUDED.config,
	metadata = COALESCE(billing.billing_provider_connections.metadata, '{}'::jsonb)
		|| jsonb_build_object('managedBy', 'application_config'),
	owner_kind = EXCLUDED.owner_kind,
	owner_id = EXCLUDED.owner_id,
	updated_at = now()
RETURNING id::text AS id
`;

function assertNoSecretMaterial(
	value: unknown,
	label: string,
): void {
	if (typeof value !== "string") {
		return;
	}
	const lowered = value.toLowerCase();
	if (
		lowered.startsWith("access_") ||
		lowered.startsWith("live_") ||
		lowered.startsWith("test_") ||
		lowered.startsWith("sk_") ||
		lowered.includes("secret")
	) {
		throw new Error(
			`Configured billing connection ${label} must not contain credential material.`,
		);
	}
}

export async function materializeConfiguredBillingConnections(input: {
	applicationId: string;
	configuredProviders?: BillingProviderConfigMap;
	provider?: "mollie";
	sql: BillingSqlExecutor;
	testMode?: boolean;
}): Promise<MaterializeConfiguredBillingConnectionsResult> {
	const applicationId = input.applicationId.trim();
	const intents = configuredBillingConnectionIntents({
		applicationId,
		configuredProviders: input.configuredProviders,
		provider: input.provider,
		testMode: input.testMode,
	});
	for (const intent of intents) {
		assertNoSecretMaterial(intent.credentialReference, "credential_reference");
		assertNoSecretMaterial(intent.accountReference, "account_reference");
	}
	const connections: MaterializedBillingConnection[] = [];
	for (const intent of intents) {
		const result = await input.sql.query(UPSERT_CONNECTION_SQL, [
			applicationId,
			intent.provider,
			intent.environment,
			intent.credentialKind,
			intent.credentialReference,
			intent.accountReference,
		]);
		const id = result.rows[0]?.id;
		if (typeof id !== "string" || id.length === 0) {
			throw new Error(
				"Configured billing connection materialization did not return an id.",
			);
		}
		connections.push({
			accountReference: intent.accountReference,
			credentialReference: intent.credentialReference,
			id,
			provider: intent.provider,
		});
	}
	return { connections };
}
